import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import { collectInvoiceReceipts, paymentReceipts } from "./invoiceReceipts";
import companyData from "const/companyData.json";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { formatDisplayDate } from "utils/dateFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlusCircle, FaPrint } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import InvoicePrintThemeToggle from "components/Invoice/InvoicePrintThemeToggle";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { toast } from "react-toastify";
import { getFileIcon, getOriginalFileName, isHiddenZeroProviderInvoiceItem } from "utils/utils";
import { refreshInvoiceVerificationCaches } from "utils/invoiceVerificationQueries";
import { getPaginatedClientData, useClientById } from "utils/clientService";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import defaultKotharLogo from "../../../assets/img/logo.png";
import InvoiceTemplate from "./InvoiceTemplate";
import useInvoiceActivationDecision from "./useInvoiceActivationDecision";
import { hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";
import {
  getInvoiceNumber as invoiceNumberOf,
  sumVerifiedPayments,
} from "utils/invoiceDashboardUtils";

const PAYMENT_STATUS = {
  UNPAID: "UNPAID",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  PAID: "PAID",
};

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const resolvePaymentStatus = (invoice) => {
  if (invoice?.paymentStatus) return invoice.paymentStatus;
  return invoice?.status === true ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;
};

const computeInvoiceTotal = (invoices = []) =>
  (invoices || []).reduce((sum, item) => {
    const lineTotal =
      item?.price != null
        ? Number(item.price) * Number(item?.quantity || 0)
        : Number(item?.total || 0);
    const gst = item?.gst === "yes" || item?.gst === "true" || item?.gst === true;
    return sum + lineTotal + (gst ? lineTotal * 0.1 : 0);
  }, 0);

const sumPayments = (payments = []) =>
  payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);

const derivePaymentStatus = (invoice) => {
  const totalPaid = sumVerifiedPayments(invoice?.payments);
  const total = computeInvoiceTotal(invoice?.invoices);
  if (totalPaid <= 0) return PAYMENT_STATUS.UNPAID;
  const paidCents = Math.round(totalPaid * 100);
  const totalCents = Math.round(total * 100);
  if (totalCents > 0 && paidCents >= totalCents) return PAYMENT_STATUS.PAID;
  return PAYMENT_STATUS.PARTIALLY_PAID;
};

const getCurrentPaymentStatus = (invoice) => {
  if (sumVerifiedPayments(invoice?.payments) > 0) return derivePaymentStatus(invoice);
  if ((invoice?.payments || []).some((p) => p?.status === "PENDING_VERIFICATION")) {
    return PAYMENT_STATUS.UNPAID;
  }
  return resolvePaymentStatus(invoice);
};

const normalizePayments = (invoice) => {
  if (Array.isArray(invoice?.payments) && invoice.payments.length) {
    // Guarantee every payment carries a receiptDocuments array (handles legacy single rows).
    return invoice.payments.map((payment) => ({
      ...payment,
      receiptDocuments: paymentReceipts(payment),
    }));
  }
  if (invoice?.partialPaidAmount || invoice?.paidDate) {
    return [
      {
        amount:
          invoice?.partialPaidAmount ||
          (resolvePaymentStatus(invoice) === PAYMENT_STATUS.PAID
            ? computeInvoiceTotal(invoice?.invoices)
            : 0),
        paymentDate: invoice?.paidDate
          ? new Date(invoice.paidDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        paymentMethod: invoice?.paymentMethod || "",
        receiptDocuments: invoice?.invoiceDocument ? [invoice.invoiceDocument] : [],
        note: invoice?.paidNote || "",
        uid: crypto.randomUUID(),
      },
    ].filter((payment) => Number(payment.amount || 0) > 0);
  }
  return [];
};

const createPaymentRow = (amount = "") => ({
  uid: crypto.randomUUID(),
  amount,
  paymentDate: new Date().toISOString().split("T")[0],
  paymentMethod: "",
  receiptDocuments: [],
  note: "",
});

const clonePaymentRows = (payments = []) =>
  payments.map((payment) => ({
    ...payment,
    uid: payment.uid || crypto.randomUUID(),
  }));

const AddInvoice = () => {
  const filter = createFilterOptions();
  const [data, setData] = useState({
    title: null,
    invoiceTo: null,
    // Invoice From (Company Details)
    invoiceFrom: null,
    companyName: "KOTHAR GROUP PTY LTD",
    invoiceFromAbn: "21 650 886 301",
    logo: null,
    location: "Unit 273/398 Pitt St, Haymarket NSW 2000",
    email: "info@kotharedu.com",
    contact: null,

    paymentInfo: {
      accountNumber: "4297 02582",
      accountName: "KOTHAR GROUP PTY LTD",
      bankDetails: "012 401",
    },
    status: false,
    paymentStatus: PAYMENT_STATUS.UNPAID,
    partialPaidAmount: null,
    abn: null,
    address: null,
    dueDate: new Date().toISOString().split("T")[0],
    contactNumber: null,
    paidDate: null,
    paymentMethod: null,
    invoiceDocument: null,
    payments: [],
    invoices: [
      {
        description: null,
        price: null,
        quantity: null,
        gst: false,
        total: null,
        uid: crypto.randomUUID(),
      },
    ],
    invoiceId: null,
    issueDate: new Date(),
    key: null,
  });
  const { state } = useLocation();
  const isClientMode = state?.mode === "client-verification";
  const clientModeClientId = state?.clientId;
  const [verificationId, setVerificationId] = useState(
    state?.invoiceId || state?.verificationId || state?.prefill?.invoiceId || null
  );
  const [confirmSend, setConfirmSend] = useState(null);

  const isPaymentLocked = (payment) =>
    payment?.status === "VERIFIED" || payment?.status === "PENDING_VERIFICATION";
  const [{ invoiceList, consultancyList }, { refetchInvoiceList, refetchConsultancy }] =
    useKothar();
  const { id } = useParams();
  const { data: clientModeClient } = useClientById(isClientMode && !id ? clientModeClientId : null);

  // Client-tab invoice creation/draft/resubmit: lock the recipient to this client and prefill
  // from either a saved draft/rejected submission or the client's own record (fresh send).
  useEffect(() => {
    if (!isClientMode || id) return;
    if (state?.prefill) {
      const payments = normalizePayments(state.prefill);
      const invoiceId = state.invoiceId || state.verificationId || state.prefill.invoiceId || null;
      if (invoiceId) setVerificationId(invoiceId);
      setData((prev) => ({
        ...prev,
        ...state.prefill,
        invoiceId,
        payments,
        paymentStatus: payments.length
          ? derivePaymentStatus({ ...state.prefill, payments })
          : resolvePaymentStatus(state.prefill),
      }));
      return;
    }
    if (clientModeClient) {
      setData((prev) => ({
        ...prev,
        invoiceTo: clientModeClient.name,
        address: clientModeClient.address || null,
        contactNumber: clientModeClient.number || null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientMode, clientModeClientId, clientModeClient, id]);

  const [forceEdit, setForceEdit] = useState(() => {
    const search = window.location?.search || "";
    return new URLSearchParams(search).get("edit") === "1";
  });
  const [useDefaultLogo, setUseDefaultLogo] = useState(true);
  const [expandedPaymentIndex, setExpandedPaymentIndex] = useState(null);
  const [paymentDeleteConfirm, setPaymentDeleteConfirm] = useState(null);
  const [markUnpaidConfirm, setMarkUnpaidConfirm] = useState(false);
  const [serverPaidStatus, setServerPaidStatus] = useState(false);
  const [isCheckingPaidStatus, setIsCheckingPaidStatus] = useState(
    Boolean(state?.item?.invoiceId || id)
  );
  const partialPaymentsDraftRef = useRef([]);
  const isPaidView =
    Boolean(id || state?.item?.invoiceId || data?.invoiceId) && serverPaidStatus && !forceEdit;
  const currentPaymentStatus = data?.paymentStatus || getCurrentPaymentStatus(data);
  const isClientLineItemsLocked =
    isClientMode && getCurrentPaymentStatus(data) === PAYMENT_STATUS.PAID;

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    if (!consultancyList?.length) {
      refetchConsultancy();
    }
    if (!invoiceList?.totalItems) {
      refetchInvoiceList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const contentRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const reactToPrintFn = useReactToPrint({
    contentRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });

  // Prior invoice recipients, deduped by invoiceTo — small/local, not the org-wide client roster.
  const uniqueInvoiceHistory = useMemo(() => {
    if (!invoiceList?.content) return [];
    const seen = new Set();
    const unique = [];

    invoiceList.content.forEach((item) => {
      const invoiceTo = item?.invoiceTo;
      // Normalize to lowercase for case-insensitive comparison
      const normalizedInvoiceTo = invoiceTo?.toString().toLowerCase().trim();

      if (normalizedInvoiceTo && !seen.has(normalizedInvoiceTo)) {
        seen.add(normalizedInvoiceTo);
        unique.push(item);
      }
    });

    return unique;
  }, [invoiceList]);

  // Active clients, searched + paginated server-side instead of mapping the full org roster.
  const {
    inputValue: invoiceToSearchInput,
    setInputValue: setInvoiceToSearchInput,
    options: invoiceToClientResults,
    loading: invoiceToClientsLoading,
    listboxProps: invoiceToListboxProps,
  } = usePaginatedSearch(fetchClientPage, { pageSize: 20 });

  const uniqueInvoiceList = useMemo(() => {
    const clientOptions = (invoiceToClientResults ?? [])
      .filter((item) => item?.active)
      .map((item) => ({
        id: item?.id,
        name: item?.name,
        invoiceTo: item?.name,
        address: item?.address,
        contactNumber: item?.number,
        type: "Client",
      }));

    return [...uniqueInvoiceHistory, ...clientOptions];
  }, [uniqueInvoiceHistory, invoiceToClientResults]);

  // Sync the search box once from a loaded/prefilled invoiceTo (e.g. edit mode) — after that,
  // typing/selecting drives both in lockstep via the Autocomplete's handlers.
  useEffect(() => {
    if (data?.invoiceTo && !invoiceToSearchInput) {
      setInvoiceToSearchInput(data.invoiceTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.invoiceTo]);

  const { mutate: getInvoiceData, isLoading: isFetchingInvoiceData } = useMutation(
    async (invoiceId) => {
      const response = await axios.get(`${API_URL}/invoice/${invoiceId}`);
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        if (data?.moduleType && data?.moduleId) {
          toast.info("This invoice belongs to a module entry and is edited from there.");
          navigate(`/admin/invoice/view/${data.invoiceId}`, { replace: true });
          return;
        }
        const payments = normalizePayments(data);
        // Prefer server paymentStatus (verified-only). Never treat PENDING as paid.
        const derived = payments.length
          ? derivePaymentStatus({ ...data, payments })
          : resolvePaymentStatus(data);
        const paymentStatus =
          data?.paymentStatus && String(data.paymentStatus).trim()
            ? String(data.paymentStatus).trim().toUpperCase()
            : derived;
        setData({
          ...data,
          payments,
          paymentStatus,
          status: paymentStatus === PAYMENT_STATUS.PAID,
          companyName:
            data?.companyName || data?.invoiceFrom?.companyName || companyData?.[0]?.name,
        });
        partialPaymentsDraftRef.current =
          paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID ? clonePaymentRows(payments) : [];
        setUseDefaultLogo(!data?.logo);
        setExpandedPaymentIndex(null);
        setServerPaidStatus(paymentStatus === PAYMENT_STATUS.PAID);
        setIsCheckingPaidStatus(false);
      },
      onError: () => {
        setIsCheckingPaidStatus(false);
        toast.error("Error loading invoice");
      },
    }
  );

  useEffect(() => {
    const invoiceId = state?.item?.invoiceId || id || (isClientMode ? state?.invoiceId : null);
    if (invoiceId) {
      if (isClientMode && state?.invoiceId) {
        setVerificationId(state.invoiceId);
      }
      setIsCheckingPaidStatus(true);
      getInvoiceData(invoiceId);
    } else {
      setIsCheckingPaidStatus(false);
    }
  }, [state, id, isClientMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name?.split(".")?.length > 1) {
      let keys = name?.split(".");
      if (keys.length === 2) {
        let key = keys[0];
        let nestedKey = keys[1];
        setData((prevState) => ({
          ...prevState,
          [key]: { ...prevState?.[key], [nestedKey]: value },
        }));
      } else if (keys.length === 3) {
        // Handle invoiceFrom.field format
        let parentKey = keys[0];
        let childKey = keys[1];
        setData((prevState) => ({
          ...prevState,
          [parentKey]: {
            ...prevState?.[parentKey],
            [childKey]: value,
          },
        }));
      }
    } else {
      setData((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canDecideActivation =
    hasPermission(P.INVOICE.VERIFY) &&
    Boolean(data?.invoiceId) &&
    data?.lifecycleStatus === "PENDING_ACTIVATION";
  const activationDecision = useInvoiceActivationDecision({
    onSettled: (invoiceId) => getInvoiceData(invoiceId),
    approveNote: "Unsaved changes on this page are not part of the activation. Save them first.",
  });
  const activationItem = {
    invoiceId: data?.invoiceId,
    invoiceTitle: data?.title,
    clientName: data?.invoiceTo,
  };
  const { mutate } = useMutation(postData, {
    onSuccess(response, { intent }) {
      queryClient.invalidateQueries(["outstandingInvoices"]);
      if (isClientMode) {
        queryClient.removeQueries(["client-invoices"]);
        queryClient.removeQueries(["client-invoice-verifications"]);
        if (intent === "draft") {
          const saved = response?.data?.data;
          if (saved?.invoiceId) {
            setVerificationId(saved.invoiceId);
            setData((prev) => ({
              ...prev,
              invoiceId: saved.invoiceId,
              payments: normalizePayments(saved),
              lifecycleStatus: saved.lifecycleStatus,
            }));
          }
          toast.success("Draft saved");
          return;
        }
        if (intent === "activation") {
          toast.success("Sent for activation");
          refreshInvoiceVerificationCaches(queryClient);
          navigate(`/admin/client/view/${clientModeClientId}`, { state: { tab: "invoices" } });
          return;
        }
        toast.success("Sent for verification");
        refreshInvoiceVerificationCaches(queryClient);
        navigate(`/admin/client/view/${clientModeClientId}`, { state: { tab: "invoices" } });
        return;
      }
      toast.success(data?.id ? "Data updated Successfully" : "Data added Successfully");
      setServerPaidStatus(Boolean(data?.status));
      setForceEdit(false);
      navigate("/admin/invoice");
      refetchInvoiceList();
    },
    onError(error, { intent }) {
      toast.error(
        isClientMode
          ? error?.response?.data?.message ||
              (intent === "draft"
                ? "Error saving draft"
                : intent === "activation"
                ? "Error sending for activation"
                : "Error submitting invoice for verification")
          : data?.id
          ? "Error Updating Data"
          : "Error Submitting Data"
      );
    },
  });
  async function postData({ payload, intent }) {
    if (isClientMode) {
      const existingId = verificationId || payload?.invoiceId;
      if (intent === "draft") {
        return axios.post(`${API_URL}/invoice-verification/draft`, {
          verificationId: existingId,
          clientId: clientModeClientId,
          invoice: { ...payload, invoiceId: existingId || payload?.invoiceId },
        });
      }
      if (intent === "activation") {
        let invoiceId = existingId;
        if (!invoiceId) {
          const draft = await axios.post(`${API_URL}/invoice-verification/draft`, {
            clientId: clientModeClientId,
            invoice: payload,
          });
          invoiceId = draft?.data?.data?.invoiceId;
          if (invoiceId) setVerificationId(invoiceId);
        }
        return axios.post(
          `${API_URL}/invoice-verification/invoices/${invoiceId}/activation/request`,
          { invoice: { ...payload, invoiceId } }
        );
      }
      if (existingId) {
        return axios.post(`${API_URL}/invoice-verification/${existingId}/send`, {
          invoice: { ...payload, invoiceId: existingId },
        });
      }
      return axios.post(`${API_URL}/invoice-verification/send`, {
        clientId: clientModeClientId,
        invoice: payload,
      });
    }
    if (data?.invoiceId) {
      return axios.put(`${API_URL}/invoice/${payload?.invoiceId}`, payload);
    }
    return axios.post(`${API_URL}/invoice`, payload);
  }

  function generateInvoiceNumber() {
    const companyPrefix =
      data?.invoiceFrom?.companyName?.replace(/\s+/g, "-").toUpperCase().substring(0, 10) || "INV";
    let count = parseInt(invoiceList?.length || "0");
    count++;
    return `${companyPrefix}-${count.toString().padStart(4, "0")}`;
  }

  function getDisplayInvoiceNumber() {
    if (!data?.invoiceId) return "";
    return invoiceNumberOf(data);
  }

  const handleSubmit = (e, intent = "send") => {
    e.preventDefault();
    const error = validateForIntent(intent);
    if (error) {
      toast.error(error);
      return;
    }
    mutate({ payload: buildSubmitPayload(), intent });
  };

  // Validate first so the confirm dialog only opens for a submittable invoice;
  // the payload is rebuilt at confirm time from the latest form state.
  const requestSend = (e, intent) => {
    e.preventDefault();
    const error = validateForIntent(intent);
    if (error) {
      toast.error(error);
      return;
    }
    setConfirmSend(intent);
  };

  const validateForIntent = (intent) => {
    if (!data?.companyName) {
      return "Please select or enter company name";
    }
    // Reviewers work the activation queue by title, and the notification they get names it.
    if (intent === "activation" && !String(data?.title || "").trim()) {
      return "Please add an invoice title before sending for activation";
    }
    if (isClientMode && intent === "send") {
      const payments = (data?.payments || []).filter(
        (payment) =>
          Number(payment?.amount || 0) > 0 &&
          (!payment.status || payment.status === "NONE" || payment.status === "REJECTED")
      );
      if (sumPayments(payments) <= 0) {
        return "You need to upload at least one receipt to send for verification, else save as draft.";
      }
    }
    return null;
  };

  const buildSubmitPayload = () => {
    const invoiceTotal = computeInvoiceTotal(data?.invoices);
    const payments = (data?.payments || [])
      .map((payment) => ({
        ...payment,
        amount: Number(payment?.amount || 0),
      }))
      .filter((payment) => payment.amount > 0);

    const verifiedPaid = sumVerifiedPayments(payments);
    const paidCents = Math.round(verifiedPaid * 100);
    const totalCents = Math.round(invoiceTotal * 100);
    const paymentStatus =
      verifiedPaid <= 0
        ? PAYMENT_STATUS.UNPAID
        : totalCents > 0 && paidCents >= totalCents
        ? PAYMENT_STATUS.PAID
        : totalCents === 0 && data?.paymentStatus === PAYMENT_STATUS.PAID
        ? PAYMENT_STATUS.PAID
        : PAYMENT_STATUS.PARTIALLY_PAID;

    return {
      ...data,
      payments,
      invoices: data?.invoices?.map((arg) => ({
        ...arg,
        total: Number(arg?.price * arg?.quantity),
      })),
      paymentStatus,
      status: paymentStatus === PAYMENT_STATUS.PAID,
      partialPaidAmount:
        paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID ? Number(verifiedPaid || 0) : null,
      invoiceNumber: generateInvoiceNumber(),
    };
  };

  const handleAddMore = () => {
    setData({
      ...data,
      invoices: [
        ...data?.invoices,
        {
          description: null,
          price: null,
          quantity: null,
          total: null,
          uuid: crypto.randomUUID(),
        },
      ],
    });
  };
  const handleDeleteInv = (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setData({
        ...data,
        invoices: [...data?.invoices?.filter((item) => item?.id !== id)],
      });
    }
  };

  const handleInvoiceInput = (e, index) => {
    const { name, value } = e.target;
    const row = data?.invoices?.find((item, i) => i === index);
    const updatedRow = {
      ...row,
      [name]: name === "gst" ? value === "yes" : value,
    };

    setData((prevState) => ({
      ...prevState,
      invoices: [
        ...prevState?.invoices?.slice(0, index),
        updatedRow,
        ...prevState?.invoices?.slice(index + 1, data.invoices?.length),
      ],
    }));
  };

  const handleMarkAsUnpaid = () => {
    if (!window.confirm("Mark this invoice as unpaid and edit?")) return;
    setData((prev) => {
      if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
        partialPaymentsDraftRef.current = clonePaymentRows(prev.payments || []);
      }
      return {
        ...prev,
        status: false,
        paymentStatus: PAYMENT_STATUS.UNPAID,
        partialPaidAmount: null,
        paidDate: null,
        paymentMethod: null,
        invoiceDocument: null,
        payments: [],
      };
    });
    setForceEdit(true);
  };

  const clearPaymentsAndMarkUnpaid = () => {
    setData((prev) => {
      if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
        partialPaymentsDraftRef.current = clonePaymentRows(prev.payments || []);
      }
      return {
        ...prev,
        status: false,
        paymentStatus: PAYMENT_STATUS.UNPAID,
        partialPaidAmount: null,
        paidDate: null,
        paymentMethod: null,
        invoiceDocument: null,
        payments: [],
      };
    });
    setExpandedPaymentIndex(null);
    setMarkUnpaidConfirm(false);
  };

  const handlePaymentStatusChange = (paymentStatus) => {
    if (paymentStatus === PAYMENT_STATUS.UNPAID) {
      if ((data?.payments || []).some((payment) => Number(payment?.amount || 0) > 0)) {
        setMarkUnpaidConfirm(true);
        return;
      }
      clearPaymentsAndMarkUnpaid();
      return;
    }

    if (paymentStatus === PAYMENT_STATUS.PAID) {
      setData((prev) => {
        if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
          partialPaymentsDraftRef.current = clonePaymentRows(prev.payments || []);
        }

        const invoiceTotal = computeInvoiceTotal(prev?.invoices);
        const nonZeroPayments = (prev.payments || []).filter(
          (payment) => Number(payment?.amount || 0) > 0
        );
        const totalPaid = sumPayments(nonZeroPayments);
        const remainingAmount = Math.max(invoiceTotal - totalPaid, 0);
        const payments =
          remainingAmount > 0
            ? [createPaymentRow(Number(remainingAmount.toFixed(2))), ...nonZeroPayments]
            : nonZeroPayments.length
            ? nonZeroPayments
            : [createPaymentRow(Number(invoiceTotal.toFixed(2)))];

        return {
          ...prev,
          paymentStatus: PAYMENT_STATUS.PAID,
          status: true,
          payments,
        };
      });
      setExpandedPaymentIndex(0);
      return;
    }

    setData((prev) => {
      const storedPartialPayments = clonePaymentRows(partialPaymentsDraftRef.current || []);
      const invoiceTotal = computeInvoiceTotal(prev?.invoices);
      const currentPayments = prev.payments || [];
      const currentPaymentsArePartial =
        currentPayments.length > 0 && sumPayments(currentPayments) < invoiceTotal;
      const payments = storedPartialPayments.length
        ? storedPartialPayments
        : currentPaymentsArePartial
        ? currentPayments
        : [createPaymentRow()];

      partialPaymentsDraftRef.current = clonePaymentRows(payments);
      return {
        ...prev,
        paymentStatus: PAYMENT_STATUS.PARTIALLY_PAID,
        status: false,
        payments,
      };
    });
    setExpandedPaymentIndex((prev) => (prev === null ? 0 : prev));
  };

  const handleAddPayment = () => {
    const nextIndex = data?.payments?.length || 0;
    setData((prev) => {
      const payments = [...(prev.payments || []), createPaymentRow()];
      if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
        partialPaymentsDraftRef.current = clonePaymentRows(payments);
      }
      return {
        ...prev,
        payments,
      };
    });
    setExpandedPaymentIndex(nextIndex);
  };

  const handlePaymentChange = (index, field, value) => {
    setData((prev) => {
      const payments = (prev.payments || []).map((payment, paymentIndex) =>
        paymentIndex === index ? { ...payment, [field]: value } : payment
      );
      if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
        partialPaymentsDraftRef.current = clonePaymentRows(payments);
      }
      return {
        ...prev,
        payments,
      };
    });
  };

  const handleDeletePayment = (index) => {
    setPaymentDeleteConfirm(index);
  };

  const confirmDeletePayment = () => {
    const index = paymentDeleteConfirm;
    if (index === null) return;
    setData((prev) => {
      const payments = (prev.payments || []).filter((_, paymentIndex) => paymentIndex !== index);
      if (prev?.paymentStatus === PAYMENT_STATUS.PARTIALLY_PAID) {
        partialPaymentsDraftRef.current = clonePaymentRows(payments);
      }
      return {
        ...prev,
        payments,
      };
    });
    setExpandedPaymentIndex((prev) => {
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
    setPaymentDeleteConfirm(null);
  };

  const [editMode, setEditMode] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    name: "Kothar Educational Services",
    accountNumber: "429702582",
    bsb: "012401",
  });
  const [tempDetails, setTempDetails] = useState(bankDetails);

  const handleEdit = () => {
    setTempDetails(bankDetails);
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSave = () => {
    setBankDetails(tempDetails);
    setEditMode(false);
  };

  const handleChange = (e) => {
    setData({
      ...data,
      paymentInfo: {
        ...data?.paymentInfo,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handlePrint = () => {
    reactToPrintFn();
  };

  if (isCheckingPaidStatus || isFetchingInvoiceData) {
    return <InvoiceStatusLoader onBack={() => navigate(-1)} />;
  }

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded  bg-app-surface"
          }
        >
          <div className="rounded-t mb-0 px-10 py-3 border-0">
            <div className="flex justify-between items-center">
              <div className="relative flex gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className={"font-semibold text-xl text-app-text"}>
                  {isPaidView ? "Paid Invoice" : data?.invoiceId ? "Edit Invoice" : "Add Invoice"}
                </h3>
                {currentPaymentStatus === PAYMENT_STATUS.PAID && (
                  <Chip label="PAID" color="success" size="small" />
                )}
                {isClientMode && (
                  <Chip
                    label={verificationId ? "Editing client invoice" : "New client invoice"}
                    color="info"
                    size="small"
                  />
                )}
              </div>
              <div className="flex gap-3 items-center">
                <InvoicePrintThemeToggle />
                {isPaidView && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setForceEdit(true)}
                  >
                    Edit
                  </Button>
                )}
                {!isClientMode && (
                  <Button variant="contained" onClick={handlePrint} startIcon={<FaPrint />}>
                    Print Invoice
                  </Button>
                )}
              </div>
            </div>
          </div>
          {isPaidView ? (
            <PaidInvoiceView data={data} />
          ) : (
            <div className="block w-full overflow-x-auto mt-2">
              <div className="flex-auto lg:px-10 py-10 pt-0">
                <form
                  className="space-y-6"
                  onSubmit={isClientMode ? (e) => requestSend(e, "send") : handleSubmit}
                >
                  {/* Invoice-Style Layout */}
                  <div className="bg-app-surface border-2 border-app-border rounded-lg shadow-lg p-8 max-w-5xl mx-auto">
                    {/* Invoice Header */}
                    <div className="grid grid-cols-5 gap-8 items-start mb-8 pb-6 border-b-2 border-app-border">
                      {/* Company Info - Left Side */}
                      <div className="col-span-3">
                        {(useDefaultLogo || data?.logo) && (
                          <div className="mb-4">
                            <img
                              src={useDefaultLogo ? defaultKotharLogo : data?.logo}
                              alt="Company Logo"
                              className="h-16 object-contain"
                            />
                          </div>
                        )}
                        <div className="space-y-3">
                          <div className="relative">
                            <InputField
                              placeholder="Invoice Title"
                              name="title"
                              required
                              type="text"
                              value={data?.title || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1"
                              outerClassName="!mb-1"
                            />
                          </div>
                          <div className="relative">
                            <Autocomplete
                              options={companyData || []}
                              value={
                                companyData?.find((c) => c?.name === data?.companyName) ??
                                companyData?.[0] ??
                                null
                              }
                              onChange={(event, newValue) => {
                                setData((prev) => ({
                                  ...prev,
                                  companyName: newValue?.name,
                                  invoiceFromAbn: newValue?.abn,
                                  logo: useDefaultLogo ? null : newValue?.logo,
                                  location: newValue?.address,
                                  email: newValue?.email,
                                  contact: newValue?.primaryContactNumber,
                                  paymentInfo: {
                                    ...prev.paymentInfo,
                                    accountName: newValue?.name,
                                    bankDetails: newValue?.paymentInfo?.bsb,
                                    accountNumber: newValue?.paymentInfo?.accountNumber || null,
                                  },
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Company"
                                  variant="standard"
                                  className="!border-0 !border-b !border-app-border !rounded-none !p-1"
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props} key={option?.id}>
                                  <span>{option?.name}</span>
                                </li>
                              )}
                              getOptionLabel={(option) => option?.name || ""}
                              getOptionValue={(option) => option?.id || ""}
                            />
                          </div>
                          <div className="relative">
                            <InputField
                              placeholder="Company Name"
                              name="companyName"
                              required
                              type="text"
                              value={data?.companyName || companyData?.[0]?.name || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1"
                              outerClassName="!mb-1"
                            />
                          </div>
                          <div className="relative">
                            <InputField
                              placeholder="Company Address"
                              name="location"
                              type="text"
                              value={data?.location || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1"
                              outerClassName="!mb-1"
                            />
                          </div>

                          <div className="relative mt-2">
                            <InputField
                              placeholder="ABN Number"
                              name="invoiceFromAbn"
                              type="text"
                              value={data?.invoiceFromAbn || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1"
                              outerClassName="!mb-1"
                            />
                          </div>

                          <div className="flex gap-2 mt-2">
                            <div className="relative flex-1">
                              <InputField
                                placeholder="Email"
                                name="email"
                                type="email"
                                value={data?.email || ""}
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                                outerClassName="!mb-0"
                              />
                            </div>
                            <div className="relative flex-1">
                              <InputField
                                placeholder="Contact"
                                name="contact"
                                type="text"
                                value={data?.contact || ""}
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                                outerClassName="!mb-0"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={useDefaultLogo}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setUseDefaultLogo(checked);
                                    if (checked) {
                                      setData((prev) => ({ ...prev, logo: null }));
                                    }
                                  }}
                                />
                              }
                              label="Use default logo of Kothar"
                            />
                            {!useDefaultLogo && (
                              <UploadFile
                                data={{
                                  logo: data?.logo,
                                }}
                                setData={(updatedData) => {
                                  setData((prev) => ({
                                    ...prev,
                                    logo: updatedData.logo,
                                  }));
                                }}
                                imageKey="logo"
                                label="Upload custom logo"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Invoice Details - Right Side */}
                      <div className="col-span-2 text-right">
                        <h1 className="text-3xl font-bold text-app-text mb-2">TAX INVOICE</h1>
                        <div className="space-y-2 mt-4 text-left">
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-app-text-secondary min-w-[100px]">
                              Invoice #:
                            </span>
                            <div className="relative flex-1">
                              <InputField
                                label=""
                                placeholder="Auto-generated on save"
                                name="_invoiceNumberDisplay"
                                type="text"
                                value={getDisplayInvoiceNumber()}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm !text-right"
                                outerClassName="!mb-0"
                                disabled
                              />
                            </div>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-app-text-secondary min-w-[100px]">
                              Date:
                            </span>
                            <div className="relative flex-1">
                              <InputField
                                label=""
                                placeholder="Issue Date"
                                name="issueDate"
                                required
                                type="date"
                                value={
                                  data?.issueDate
                                    ? new Date(data.issueDate).toISOString().split("T")[0]
                                    : ""
                                }
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm !text-right"
                                outerClassName="!mb-0"
                              />
                            </div>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-sm font-semibold text-app-text-secondary min-w-[100px]">
                              Due Date:
                            </span>
                            <div className="relative flex-1">
                              <InputField
                                label=""
                                placeholder="Due Date"
                                name="dueDate"
                                required
                                type="date"
                                value={
                                  data?.dueDate
                                    ? new Date(data.dueDate).toISOString().split("T")[0]
                                    : ""
                                }
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm !text-right"
                                outerClassName="!mb-0"
                              />
                            </div>
                          </div>
                          <div className="flex justify-between gap-4 mt-3">
                            <span className="text-sm font-semibold text-app-text-secondary">
                              Status:
                            </span>
                            <FormControl sx={{ display: "flex", width: "auto" }}>
                              <RadioGroup
                                row
                                name="paymentStatus"
                                onChange={(e) => handlePaymentStatusChange(e.target.value)}
                                value={currentPaymentStatus}
                              >
                                <FormControlLabel
                                  value={PAYMENT_STATUS.PAID}
                                  control={<Radio size="small" />}
                                  label={
                                    <span className="text-xs text-green-600 font-semibold">
                                      Paid
                                    </span>
                                  }
                                />
                                <FormControlLabel
                                  value={PAYMENT_STATUS.PARTIALLY_PAID}
                                  control={<Radio size="small" />}
                                  label={
                                    <span className="text-xs text-amber-600 font-semibold">
                                      Partial
                                    </span>
                                  }
                                />
                                <FormControlLabel
                                  value={PAYMENT_STATUS.UNPAID}
                                  control={<Radio size="small" />}
                                  label={
                                    <span className="text-xs text-red-600 font-semibold">
                                      Unpaid
                                    </span>
                                  }
                                />
                              </RadioGroup>
                            </FormControl>
                          </div>
                          {(currentPaymentStatus !== PAYMENT_STATUS.UNPAID ||
                            (data?.payments || []).length > 0) && (
                            <div className="mt-3 pt-3 border-t border-app-border space-y-3 text-left">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-app-text">
                                    Payment History
                                  </p>
                                  <p className="text-xs text-app-text-secondary">
                                    Paid ${sumVerifiedPayments(data?.payments).toFixed(2)} of $
                                    {computeInvoiceTotal(data?.invoices).toFixed(2)}
                                  </p>
                                </div>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleAddPayment}
                                  startIcon={<FaPlusCircle />}
                                >
                                  Add
                                </Button>
                              </div>
                              {(data?.payments || []).map((payment, paymentIndex) => (
                                <div
                                  key={payment.id || payment.uid || paymentIndex}
                                  className="rounded border border-app-border bg-app-surface-muted overflow-hidden"
                                >
                                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-app-text">
                                          ${Number(payment?.amount || 0).toFixed(2)}
                                        </span>
                                        {payment?.status && (
                                          <span className="text-xs text-app-text-secondary">
                                            {payment.status}
                                          </span>
                                        )}
                                        <span className="text-xs text-app-text-secondary">
                                          {payment?.paymentDate || "No date"}
                                        </span>
                                      </div>
                                      <div className="truncate text-xs text-app-text-secondary">
                                        {payment?.paymentMethod || "No payment method"}
                                        {payment?.note ? ` · ${payment.note}` : ""}
                                      </div>
                                      {payment?.status === "REJECTED" &&
                                        payment?.rejectionFeedback && (
                                          <div className="text-xs text-red-600">
                                            Rejected
                                            {payment?.rejectedByName
                                              ? ` by ${payment.rejectedByName}`
                                              : ""}
                                            : {payment.rejectionFeedback}
                                          </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {paymentReceipts(payment).length > 0 && (
                                        <Tooltip
                                          title={
                                            paymentReceipts(payment).length > 1
                                              ? `Open first of ${
                                                  paymentReceipts(payment).length
                                                } receipts`
                                              : "Open receipt"
                                          }
                                        >
                                          <IconButton
                                            size="small"
                                            component="a"
                                            href={paymentReceipts(payment)[0]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <OpenInNewIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {!(isClientMode && isPaymentLocked(payment)) && (
                                        <Tooltip
                                          title={
                                            expandedPaymentIndex === paymentIndex
                                              ? "Collapse"
                                              : "Edit payment"
                                          }
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              setExpandedPaymentIndex((prev) =>
                                                prev === paymentIndex ? null : paymentIndex
                                              )
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {!(isClientMode && isPaymentLocked(payment)) && (
                                        <Tooltip title="Remove payment">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeletePayment(paymentIndex)}
                                          >
                                            <DeleteIcon className="text-red-500" fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>

                                  {expandedPaymentIndex === paymentIndex && (
                                    <div className="border-t border-app-border p-3 space-y-2">
                                      <div className="grid grid-cols-2 gap-2">
                                        <InputField
                                          label="Amount"
                                          type="number"
                                          value={payment?.amount ?? ""}
                                          onChange={(e) =>
                                            handlePaymentChange(
                                              paymentIndex,
                                              "amount",
                                              e.target.value
                                            )
                                          }
                                          className="!border !border-app-border !rounded !p-1.5 !text-sm"
                                          outerClassName="!mb-0"
                                          disabled={isClientMode && isPaymentLocked(payment)}
                                        />
                                        <InputField
                                          label="Date"
                                          type="date"
                                          value={payment?.paymentDate || ""}
                                          onChange={(e) =>
                                            handlePaymentChange(
                                              paymentIndex,
                                              "paymentDate",
                                              e.target.value
                                            )
                                          }
                                          className="!border !border-app-border !rounded !p-1.5 !text-sm"
                                          outerClassName="!mb-0"
                                          disabled={isClientMode && isPaymentLocked(payment)}
                                        />
                                      </div>
                                      <InputField
                                        label="Payment Method"
                                        placeholder="e.g. Bank Transfer, Card"
                                        type="text"
                                        value={payment?.paymentMethod || ""}
                                        onChange={(e) =>
                                          handlePaymentChange(
                                            paymentIndex,
                                            "paymentMethod",
                                            e.target.value
                                          )
                                        }
                                        className="!border !border-app-border !rounded !p-1.5 !text-sm"
                                        outerClassName="!mb-0"
                                        disabled={isClientMode && isPaymentLocked(payment)}
                                      />
                                      <InputField
                                        label="Note"
                                        placeholder="Optional note"
                                        type="text"
                                        value={payment?.note || ""}
                                        onChange={(e) =>
                                          handlePaymentChange(paymentIndex, "note", e.target.value)
                                        }
                                        className="!border !border-app-border !rounded !p-1.5 !text-sm"
                                        outerClassName="!mb-0"
                                        disabled={isClientMode && isPaymentLocked(payment)}
                                      />
                                      <UploadFile
                                        data={{
                                          receiptDocuments: paymentReceipts(payment),
                                        }}
                                        setData={(updatedData) => {
                                          handlePaymentChange(
                                            paymentIndex,
                                            "receiptDocuments",
                                            updatedData.receiptDocuments || []
                                          );
                                        }}
                                        imageKey="receiptDocuments"
                                        label="Payment Receipts"
                                        type="multiple"
                                        required={false}
                                      />
                                      <div className="flex justify-end">
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={() => setExpandedPaymentIndex(null)}
                                        >
                                          Done
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {data?.payments?.length === 0 && (
                                <div className="rounded border border-dashed border-app-border px-3 py-4 text-center text-sm text-app-text-secondary">
                                  No payments recorded.
                                </div>
                              )}
                              {(data?.payments || []).length > 1 && (
                                <div className="text-xs text-app-text-secondary">
                                  Select the edit icon on a payment to view receipt and details.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bill To Section */}
                    <div className="grid grid-cols-2 gap-8 mb-8">
                      <div>
                        <h3 className="text-sm font-bold text-app-text uppercase mb-3">Bill To:</h3>
                        <div className="space-y-2">
                          <Autocomplete
                            freeSolo
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            size="small"
                            disabled={isClientMode}
                            loading={invoiceToClientsLoading}
                            options={uniqueInvoiceList || []}
                            inputValue={invoiceToSearchInput}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "reset") return;
                              setInvoiceToSearchInput(newInputValue);
                            }}
                            value={
                              data?.invoiceTo
                                ? uniqueInvoiceList?.find(
                                    (item) => item?.invoiceTo === data?.invoiceTo
                                  ) || data?.invoiceTo
                                : null
                            }
                            onChange={(event, newValue) => {
                              if (typeof newValue === "string") {
                                setData((prev) => ({
                                  ...prev,
                                  invoiceTo: newValue,
                                  address: null,
                                  contactNumber: null,
                                }));
                                setInvoiceToSearchInput(newValue);
                              } else if (newValue && typeof newValue === "object") {
                                if (newValue.inputValue) {
                                  setData((prev) => ({
                                    ...prev,
                                    invoiceTo: newValue.inputValue,
                                    address: null,
                                    contactNumber: null,
                                  }));
                                  setInvoiceToSearchInput(newValue.inputValue);
                                } else if (newValue.invoiceTo) {
                                  setData((prev) => ({
                                    ...prev,
                                    invoiceTo: newValue.invoiceTo,
                                    address: newValue.address || null,
                                    contactNumber: newValue.contactNumber || null,
                                  }));
                                  setInvoiceToSearchInput(newValue.invoiceTo);
                                }
                              } else {
                                setData((prev) => ({
                                  ...prev,
                                  invoiceTo: null,
                                  address: null,
                                  contactNumber: null,
                                }));
                              }
                            }}
                            filterOptions={(options, params) => {
                              // History portion is small/local — filter it client-side. The
                              // client portion is already server-filtered by the search term.
                              const filtered = filter(uniqueInvoiceHistory, params);
                              const clientPart = options.filter((o) => o?.type === "Client");
                              const combined = [...filtered, ...clientPart];
                              const { inputValue } = params;
                              const isExisting = combined.some(
                                (option) => inputValue === option?.invoiceTo
                              );
                              if (inputValue !== "" && !isExisting) {
                                combined.push({
                                  inputValue,
                                  title: `Add "${inputValue}"`,
                                });
                              }
                              return combined;
                            }}
                            ListboxProps={{ ...invoiceToListboxProps }}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") {
                                return option;
                              }
                              if (option.inputValue) {
                                return option.inputValue;
                              }
                              return option?.invoiceTo || option?.title || "";
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option?.id || option?.inputValue}>
                                <span>
                                  {option?.inputValue
                                    ? option.title
                                    : option?.invoiceTo + " - " + (option?.type ?? "Invoice") ||
                                      option?.title + " - " + (option?.type ?? "Invoice") ||
                                      ""}
                                </span>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Recipient Name"
                                required
                                variant="standard"
                                sx={{
                                  "& .MuiInput-underline:before": {
                                    borderBottomColor: "#d1d5db",
                                  },
                                  "& .MuiInput-underline:hover:before": {
                                    borderBottomColor: "#9ca3af",
                                  },
                                }}
                              />
                            )}
                          />
                          <div className="relative">
                            <InputField
                              label=""
                              placeholder="Address"
                              name="address"
                              required
                              type="text"
                              value={data?.address || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                              outerClassName="!mb-1"
                            />
                          </div>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <InputField
                                label=""
                                placeholder="Contact"
                                name="contactNumber"
                                required
                                type="text"
                                value={data?.contactNumber || ""}
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                                outerClassName="!mb-0"
                              />
                            </div>
                            <div className="relative flex-1">
                              <InputField
                                label=""
                                placeholder="ABN (optional)"
                                name="abn"
                                type="text"
                                value={data?.abn || ""}
                                onChange={handleInputChange}
                                className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                                outerClassName="!mb-0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Information - Right Side */}
                      <div>
                        <h3 className="text-sm font-bold text-app-text uppercase mb-3">
                          Payment Details:
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="relative">
                            <InputField
                              label=""
                              placeholder="Account Name"
                              name="paymentInfo.accountName"
                              value={data?.paymentInfo?.accountName || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                              outerClassName="!mb-1"
                              disabled={isClientMode}
                            />
                          </div>
                          <div className="relative">
                            <InputField
                              label=""
                              placeholder="BSB"
                              name="paymentInfo.bankDetails"
                              value={data?.paymentInfo?.bankDetails || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                              outerClassName="!mb-1"
                              disabled={isClientMode}
                            />
                          </div>
                          <div className="relative">
                            <InputField
                              label=""
                              placeholder="Account Number"
                              name="paymentInfo.accountNumber"
                              value={data?.paymentInfo?.accountNumber || ""}
                              onChange={handleInputChange}
                              className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                              outerClassName="!mb-0"
                              disabled={isClientMode}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Invoice Items Table */}
                    <div className="mb-6">
                      {data?.invoices?.some((item) => item?.providerAdmissionTermId) &&
                        (() => {
                          const firstProviderItem = data.invoices.find(
                            (item) => item?.providerAdmissionTermId && item?.accountProviderId
                          );
                          return (
                            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-primary flex items-center gap-2">
                              <span>🔒</span>
                              <span>
                                Some items are linked to Provider Accounts. Price and GST for these
                                items can only be changed from{" "}
                                <strong
                                  className="underline cursor-pointer hover:text-blue-900"
                                  onClick={() =>
                                    navigate(
                                      "/admin/account/provider/add",
                                      firstProviderItem
                                        ? {
                                            state: {
                                              item: {
                                                accountProviderId:
                                                  firstProviderItem.accountProviderId,
                                                universityName: firstProviderItem.universityName,
                                                universityId: firstProviderItem.universityId,
                                              },
                                            },
                                          }
                                        : undefined
                                    )
                                  }
                                >
                                  Provider Accounts
                                </strong>
                                . Quantity is read-only for provider-linked lines.
                              </span>
                            </div>
                          );
                        })()}
                      {data?.invoices?.some(
                        (item) => item?.providerAdmissionTermId && item?.descriptionCustomized
                      ) && (
                        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                          Some lines have custom descriptions that may reference old term labels or
                          amounts after provider account or term changes. Review descriptions before
                          sending or printing.
                        </div>
                      )}
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-app-surface-muted border-b-2 border-app-border">
                            <th className="text-left py-3 px-4 font-semibold text-app-text uppercase text-xs">
                              Description
                            </th>
                            <th className="text-center py-3 px-4 font-semibold text-app-text uppercase text-xs w-24">
                              Quantity
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-app-text uppercase text-xs w-28">
                              Unit Price
                            </th>
                            <th className="text-center py-3 px-4 font-semibold text-app-text uppercase text-xs w-20">
                              GST
                            </th>
                            <th className="text-right py-3 px-4 font-semibold text-app-text uppercase text-xs w-32">
                              Total
                            </th>
                            <th className="text-center py-3 px-4 w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data?.invoices?.map((item, index) => {
                            if (isHiddenZeroProviderInvoiceItem(item)) return null;
                            const isPaidItem = item?.status === true;
                            const isProviderItem = !!item?.providerAdmissionTermId;
                            const isGstYes =
                              item?.gst === "yes" || item?.gst === "true" || item?.gst === true;
                            const isLocked =
                              isPaidItem || isProviderItem || isClientLineItemsLocked;
                            return (
                              <tr
                                key={index}
                                className={`border-b border-app-border ${
                                  isPaidItem
                                    ? "bg-green-50 opacity-80"
                                    : "hover:bg-app-surface-hover"
                                }`}
                              >
                                <td className="py-3 px-4">
                                  {isPaidItem || isClientLineItemsLocked ? (
                                    <span className="text-sm text-app-text-secondary">
                                      {item?.description || "-"}
                                    </span>
                                  ) : (
                                    <InputField
                                      label=""
                                      placeholder="Item description"
                                      name="description"
                                      required
                                      type="text"
                                      value={item?.description || ""}
                                      onChange={(e) => handleInvoiceInput(e, index)}
                                      className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm"
                                      outerClassName="!mb-0"
                                    />
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {isLocked ? (
                                    <span className="text-sm text-center block">
                                      {item?.quantity}
                                    </span>
                                  ) : (
                                    <InputField
                                      type="number"
                                      placeholder="Qty"
                                      name="quantity"
                                      required
                                      value={item?.quantity || ""}
                                      onChange={(e) => handleInvoiceInput(e, index)}
                                      className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm !text-center"
                                      outerClassName="!mb-0"
                                    />
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {isLocked ? (
                                    <Tooltip
                                      title={
                                        isProviderItem && !isPaidItem
                                          ? "Change from Provider Accounts"
                                          : ""
                                      }
                                      arrow
                                    >
                                      <span
                                        className={`text-sm text-right block ${
                                          isProviderItem && !isPaidItem
                                            ? "cursor-pointer hover:text-primary"
                                            : ""
                                        }`}
                                        onClick={
                                          isProviderItem && !isPaidItem
                                            ? () =>
                                                navigate("/admin/account/provider/add", {
                                                  state: {
                                                    item: {
                                                      accountProviderId: item?.accountProviderId,
                                                      universityName: item?.universityName,
                                                      universityId: item?.universityId,
                                                    },
                                                    targetClientId: item?.clientId,
                                                  },
                                                })
                                            : undefined
                                        }
                                      >
                                        {item?.price}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <InputField
                                      type="number"
                                      placeholder="Price"
                                      name="price"
                                      required
                                      value={item?.price || ""}
                                      onChange={(e) => handleInvoiceInput(e, index)}
                                      className="!border-0 !border-b !border-app-border !rounded-none !p-1 !text-sm !text-right"
                                      outerClassName="!mb-0"
                                    />
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {isLocked ? (
                                    <Tooltip
                                      title={
                                        isProviderItem && !isPaidItem
                                          ? "Change from Provider Accounts"
                                          : ""
                                      }
                                      arrow
                                    >
                                      <span
                                        className={`text-xs text-center block ${
                                          isProviderItem && !isPaidItem
                                            ? "text-primary cursor-pointer hover:text-blue-800"
                                            : "text-app-text-secondary"
                                        }`}
                                        onClick={
                                          isProviderItem && !isPaidItem
                                            ? () =>
                                                navigate("/admin/account/provider/add", {
                                                  state: {
                                                    item: {
                                                      accountProviderId: item?.accountProviderId,
                                                      universityName: item?.universityName,
                                                      universityId: item?.universityId,
                                                    },
                                                    targetClientId: item?.clientId,
                                                  },
                                                })
                                            : undefined
                                        }
                                      >
                                        {isGstYes ? "Yes" : "No"}
                                        {isProviderItem && !isPaidItem && (
                                          <span className="ml-1 text-app-text-muted">🔒</span>
                                        )}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <FormControl sx={{ display: "flex", width: "100%" }}>
                                      <RadioGroup
                                        row
                                        name="gst"
                                        onChange={(e) => handleInvoiceInput(e, index)}
                                        value={isGstYes ? "yes" : "no"}
                                        sx={{ justifyContent: "center" }}
                                      >
                                        <FormControlLabel
                                          value="yes"
                                          control={<Radio size="small" />}
                                          label={<span className="text-xs">Yes</span>}
                                        />
                                        <FormControlLabel
                                          value="no"
                                          control={<Radio size="small" />}
                                          label={<span className="text-xs">No</span>}
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right font-semibold">
                                  {(Number(item?.price || 0) * Number(item?.quantity || 0)).toFixed(
                                    2
                                  )}
                                  {isPaidItem && (
                                    <span className="ml-1 text-xs text-green-600 font-normal">
                                      ✓ Paid
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    {isProviderItem && !isPaidItem && (
                                      <Tooltip title="Edit in Provider Accounts" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            navigate("/admin/account/provider/add", {
                                              state: {
                                                item: {
                                                  accountProviderId: item?.accountProviderId,
                                                  universityName: item?.universityName,
                                                  universityId: item?.universityId,
                                                },
                                                targetClientId: item?.clientId,
                                              },
                                            })
                                          }
                                        >
                                          <EditIcon className="text-blue-500" fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {isPaidItem || isClientLineItemsLocked ? (
                                      <Tooltip
                                        title={
                                          isClientLineItemsLocked
                                            ? "Cannot delete items on a fully verified paid client invoice"
                                            : "Cannot delete paid item"
                                        }
                                      >
                                        <span>
                                          <DeleteIcon className="text-gray-300" fontSize="small" />
                                        </span>
                                      </Tooltip>
                                    ) : isProviderItem ? (
                                      <Tooltip title="Cannot delete provider-linked item">
                                        <span>
                                          <DeleteIcon className="text-gray-300" fontSize="small" />
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteInv(item?.id)}
                                        >
                                          <DeleteIcon className="text-red-500" fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {data?.invoices?.length > 0 &&
                            data.invoices.every((item) =>
                              isHiddenZeroProviderInvoiceItem(item)
                            ) && (
                              <tr>
                                <td colSpan={6} className="py-6 text-center text-app-text-muted">
                                  No line items with an amount to show
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                      {!isClientLineItemsLocked && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddMore}
                          className="mt-3"
                          startIcon={<FaPlusCircle />}
                        >
                          Add Item
                        </Button>
                      )}
                    </div>

                    {/* Totals Section */}
                    <div className="flex justify-end mb-6">
                      <div className="w-80 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold text-app-text">Subtotal:</span>
                          <span className="font-semibold">
                            $
                            {data?.invoices
                              ?.reduce(
                                (sum, item) =>
                                  sum + Number(item?.price || 0) * Number(item?.quantity || 0),
                                0
                              )
                              .toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold text-app-text">GST (10%):</span>
                          <span className="font-semibold">
                            $
                            {data?.invoices
                              ?.filter(
                                (item) =>
                                  item?.gst === "yes" || item?.gst === "true" || item?.gst === true
                              )
                              ?.reduce(
                                (sum, item) =>
                                  sum +
                                  Number(item?.price || 0) * Number(item?.quantity || 0) * 0.1,
                                0
                              )
                              .toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-app-border">
                          <span>Total AUD:</span>
                          <span>
                            $
                            {(
                              data?.invoices?.reduce(
                                (sum, item) =>
                                  sum + Number(item?.price || 0) * Number(item?.quantity || 0),
                                0
                              ) +
                              data?.invoices
                                ?.filter(
                                  (item) =>
                                    item?.gst === "yes" ||
                                    item?.gst === "true" ||
                                    item?.gst === true
                                )
                                ?.reduce(
                                  (sum, item) =>
                                    sum +
                                    Number(item?.price || 0) * Number(item?.quantity || 0) * 0.1,
                                  0
                                )
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-4 mt-6 pt-6 border-t-2 border-app-border">
                    <Button variant="outlined" onClick={() => navigate(-1)} to="">
                      Cancel
                    </Button>
                    {isClientMode && (
                      <Button variant="outlined" onClick={(e) => handleSubmit(e, "draft")}>
                        Save Draft
                      </Button>
                    )}
                    {isClientMode && (
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={(e) => requestSend(e, "activation")}
                      >
                        Send for Activation
                      </Button>
                    )}
                    {canDecideActivation && (
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={activationDecision.isDeciding}
                        onClick={() => activationDecision.openReject(activationItem)}
                      >
                        Reject Activation
                      </Button>
                    )}
                    {canDecideActivation && (
                      <Button
                        variant="contained"
                        color="success"
                        disabled={activationDecision.isDeciding}
                        onClick={() => activationDecision.openApprove(activationItem)}
                      >
                        Activate
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      type={isClientMode ? "button" : "submit"}
                      onClick={isClientMode ? (e) => requestSend(e, "send") : undefined}
                    >
                      {isClientMode ? "Send for Verification" : "Save Invoice"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {contentRef && <InvoiceTemplate printData={data} contentRef={contentRef} />}
      {activationDecision.dialogs}
      <Dialog
        open={Boolean(confirmSend)}
        onClose={() => setConfirmSend(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmSend === "activation" ? "Send for Activation?" : "Send for Verification?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmSend === "activation"
              ? "This invoice will be sent to the accounts team for activation. It stays non-emailable until approved."
              : "The entered payments will be sent to the accounts team for verification. The invoice goes live only once a payment is verified."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              const intent = confirmSend;
              setConfirmSend(null);
              handleSubmit(e, intent);
            }}
            variant="contained"
          >
            Confirm Send
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={markUnpaidConfirm}
        onClose={() => setMarkUnpaidConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark Invoice Unpaid?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all recorded payment entries from this invoice after you save. Use this
            only if the existing paid or partial payment history should be cleared.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkUnpaidConfirm(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={clearPaymentsAndMarkUnpaid} variant="contained" color="error">
            Mark Unpaid
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={paymentDeleteConfirm !== null}
        onClose={() => setPaymentDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This payment entry will be removed from the invoice after you save. This can change the
            invoice paid or partial status.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDeleteConfirm(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmDeletePayment} variant="contained" color="error">
            Remove Payment
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddInvoice;

const InvoiceStatusLoader = ({ onBack }) => (
  <div className="flex flex-wrap mt-4 dashBody">
    <div className="w-full mb-12 px-4">
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
        <div className="rounded-t mb-0 px-10 py-3 border-0">
          <div className="flex justify-between items-center">
            <div className="relative flex gap-4 items-center">
              <IoArrowBack className="text-xl cursor-pointer" onClick={onBack} />
              <h3 className="font-semibold text-xl text-app-text">Loading Invoice</h3>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[420px] gap-4">
          <CircularProgress size={36} />
          <div className="text-center">
            <p className="font-semibold text-app-text">Checking invoice status</p>
            <p className="text-sm text-app-text-muted">
              Please wait while we load the latest payment details.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const safeNum = (v) => Number(v || 0);

const computeTotals = (invoices = []) => {
  const subtotal = invoices.reduce((sum, i) => sum + safeNum(i?.price) * safeNum(i?.quantity), 0);
  const gst = invoices
    .filter((i) => i?.gst === "yes" || i?.gst === "true" || i?.gst === true)
    .reduce((sum, i) => sum + safeNum(i?.price) * safeNum(i?.quantity) * 0.1, 0);
  return { subtotal, gst, total: subtotal + gst };
};

const isImagePath = (filePath) => {
  if (!filePath) return false;
  const ext = filePath.split("?")[0].split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
};

const ReceiptCard = ({ url }) => {
  const isImage = isImagePath(url);
  const name = getOriginalFileName(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-lg border border-app-border bg-app-surface hover:shadow-md transition overflow-hidden"
    >
      <div className="h-40 bg-app-surface-muted flex items-center justify-center">
        {isImage ? (
          <img src={url} alt={name} className="max-h-40 max-w-full object-contain" />
        ) : (
          <div className="text-5xl">{getFileIcon(url)}</div>
        )}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-app-border">
        <span className="text-lg shrink-0">{getFileIcon(url)}</span>
        <span className="text-sm text-app-text truncate flex-1" title={name}>
          {name}
        </span>
        <OpenInNewIcon fontSize="small" className="text-app-text-muted group-hover:text-primary" />
      </div>
    </a>
  );
};

const PaidInvoiceView = ({ data }) => {
  const { subtotal, gst, total } = computeTotals(data?.invoices);
  const payments = Array.isArray(data?.payments) ? data.payments : [];
  const receipts = collectInvoiceReceipts(data);

  const fmt = (val) => `$${safeNum(val).toFixed(2)}`;
  const fmtDate = (v) => formatDisplayDate(v, "—");

  return (
    <div className="px-6 md:px-10 py-8 space-y-6">
      {/* Payment summary banner */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center text-2xl font-bold">
            ✓
          </div>
          <div>
            <p className="text-emerald-700 font-semibold text-lg">Payment Received</p>
            <p className="text-sm text-emerald-700/80">
              This invoice has been marked as paid. Editing is locked.
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase font-semibold text-emerald-700/80">Amount Paid</p>
          <p className="text-3xl font-bold text-emerald-700">{fmt(total)}</p>
        </div>
      </div>

      {/* Payment + Invoice meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetaCard label="Last Paid Date" value={fmtDate(data?.paidDate)} />
        <MetaCard label="Last Method" value={data?.paymentMethod || "—"} />
        <MetaCard label="Issue Date" value={fmtDate(data?.issueDate)} />
        <MetaCard label="Due Date" value={fmtDate(data?.dueDate)} />
      </div>

      <Panel title={`Payment History (${payments.length})`}>
        {payments.length ? (
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-app-surface-muted border-b border-app-border">
                  <th className="text-left py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                    Date
                  </th>
                  <th className="text-right py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                    Amount
                  </th>
                  <th className="text-left py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                    Method
                  </th>
                  <th className="text-left py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, idx) => (
                  <tr key={payment?.id || idx} className="border-b border-app-border">
                    <td className="py-2.5 px-4 text-app-text">{fmtDate(payment?.paymentDate)}</td>
                    <td className="py-2.5 px-4 text-right font-semibold text-app-text">
                      {fmt(payment?.amount)}
                    </td>
                    <td className="py-2.5 px-4 text-app-text">{payment?.paymentMethod || "—"}</td>
                    <td className="py-2.5 px-4 text-app-text">{payment?.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-app-text-muted italic">No payment entries recorded.</div>
        )}
      </Panel>

      {/* Invoice header (from / to) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="From">
          <p className="font-semibold text-app-text">{data?.companyName}</p>
          {data?.location && <p className="text-sm text-app-text-secondary">{data.location}</p>}
          {data?.invoiceFromAbn && (
            <p className="text-sm text-app-text-secondary">ABN: {data.invoiceFromAbn}</p>
          )}
          {data?.email && <p className="text-sm text-app-text-secondary">{data.email}</p>}
          {data?.contact && <p className="text-sm text-app-text-secondary">{data.contact}</p>}
        </Panel>
        <Panel title="Bill To">
          <p className="font-semibold text-app-text">{data?.invoiceTo || "—"}</p>
          {data?.address && <p className="text-sm text-app-text-secondary">{data.address}</p>}
          {data?.contactNumber && (
            <p className="text-sm text-app-text-secondary">{data.contactNumber}</p>
          )}
          {data?.abn && <p className="text-sm text-app-text-secondary">ABN: {data.abn}</p>}
        </Panel>
      </div>

      {/* Items table */}
      <Panel title="Line Items">
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-app-surface-muted border-b border-app-border">
                <th className="text-left py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                  Description
                </th>
                <th className="text-center py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-24">
                  Qty
                </th>
                <th className="text-right py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-28">
                  Unit Price
                </th>
                <th className="text-center py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-20">
                  GST
                </th>
                <th className="text-right py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-32">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.invoices?.filter((item) => !isHiddenZeroProviderInvoiceItem(item))?.length ? (
                data.invoices
                  .filter((item) => !isHiddenZeroProviderInvoiceItem(item))
                  .map((item, idx) => (
                    <tr key={item?.id || item?.uid || idx} className="border-b border-app-border">
                      <td className="py-2.5 px-4 text-app-text">{item?.description || "—"}</td>
                      <td className="py-2.5 px-4 text-center text-app-text">
                        {item?.quantity ?? "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right text-app-text">{fmt(item?.price)}</td>
                      <td className="py-2.5 px-4 text-center text-app-text">
                        {item?.gst === "yes" || item?.gst === "true" || item?.gst === true
                          ? "10%"
                          : "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right font-semibold text-app-text">
                        {fmt(safeNum(item?.price) * safeNum(item?.quantity))}
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-app-text-muted">
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mt-4">
          <div className="w-full md:w-80 space-y-2">
            <div className="flex justify-between text-sm text-app-text-secondary">
              <span>Subtotal</span>
              <span className="font-semibold">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-app-text-secondary">
              <span>GST (10%)</span>
              <span className="font-semibold">{fmt(gst)}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t-2 border-app-border text-app-text">
              <span>Total AUD</span>
              <span>{fmt(total)}</span>
            </div>
          </div>
        </div>
      </Panel>

      {/* Receipts */}
      <Panel
        title={`Receipts (${receipts.length})`}
        subtitle="Files uploaded when this invoice was marked as paid"
      >
        {receipts.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {receipts.map((url) => (
              <ReceiptCard key={url} url={url} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-app-text-muted italic">No receipts were uploaded.</div>
        )}
      </Panel>
    </div>
  );
};

const MetaCard = ({ label, value }) => (
  <div className="bg-app-surface border border-app-border rounded-lg px-4 py-3">
    <p className="text-xs uppercase font-semibold tracking-wide text-app-text-muted">{label}</p>
    <p className="text-base font-semibold text-app-text mt-1">{value}</p>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <div className="bg-app-surface border border-app-border rounded-lg p-5">
    <div className="mb-4">
      <h3 className="text-sm font-bold text-app-text uppercase tracking-wide">{title}</h3>
      {subtitle && <p className="text-xs text-app-text-muted mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);
